/**
 * Webhooks card on the Intake Channels page.
 *
 * Shows the endpoint URL, the inbound enable toggle, a one-time secret
 * reveal + regenerate button, outbound URL + event subscriptions, last
 * inbound/outbound status, and a sample payload block.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Webhook, ShieldCheck, AlertCircle, Loader2, RefreshCw, KeyRound,
  Copy, ExternalLink, CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ALL_EVENTS = [
  "draft.created",
  "draft.staff_approved",
  "confirmation.sent",
  "booking.customer_confirmed",
  "booking.customer_requested_change",
  "booking.cancelled",
];

export default function WebhooksCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secretOnce, setSecretOnce] = useState(null);
  const [showSecretOnce, setShowSecretOnce] = useState(false);
  const [outboundUrl, setOutboundUrl] = useState("");
  const [outboundEvents, setOutboundEvents] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intake/webhooks/config");
      setCfg(data);
      setOutboundUrl(data.outbound?.url || "");
      setOutboundEvents(data.outbound?.events || []);
      const { data: lg } = await api.get("/intake/webhooks/outbound/log?limit=10");
      setLog(lg.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleEnable = async (enable) => {
    setBusy(true);
    try {
      await api.post("/intake/webhooks/enable", { enabled: enable });
      if (enable && !cfg?.has_secret) {
        const { data } = await api.get("/intake/webhooks/secret-once");
        setSecretOnce(data.secret); setShowSecretOnce(true);
      }
      toast.success(enable ? "Inbound webhook enabled." : "Webhook disabled.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    if (!confirm("Generate a new webhook secret? Old secret stops working immediately."))
      return;
    setBusy(true);
    try {
      const { data } = await api.post("/intake/webhooks/regenerate-secret", {});
      setSecretOnce(data.secret); setShowSecretOnce(true);
      toast.success("New secret generated: paste it into your tooling now.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveOutbound = async () => {
    setBusy(true);
    try {
      await api.put("/intake/webhooks/outbound", {
        url: outboundUrl || null,
        events: outboundEvents,
        headers: {},
      });
      toast.success("Outbound webhook updated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const exampleBody = useMemo(() => JSON.stringify({
    name: "Maria Cruz",
    phone: "09181234567",
    email: "maria@example.com",
    vehicle: "Honda Click",
    start: "2026-09-05T09:00:00+08:00",
    end: "2026-09-06T09:00:00+08:00",
    price: 800,
    deposit: 500,
    external_reference: "your-system-id-1234",
  }, null, 2), []);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5"
          data-testid="webhooks-card">
      <header className="flex items-start gap-3">
        <Webhook className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold">Webhooks (Zapier / Make / Forms)</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Receive booking intake from external systems via a secure
            HMAC-signed POST. Every inbound payload becomes a Booking
            Intake DRAFT: staff still reviews + sends confirmation
            manually or auto.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <Row label="Inbound enabled"
                value={cfg.enabled ? <ShieldCheck className="w-4 h-4 text-emerald-700" /> : <AlertCircle className="w-4 h-4 text-slate-400" />}
                action={
                  <button onClick={() => toggleEnable(!cfg.enabled)} disabled={busy}
                          data-testid="webhook-enable-toggle"
                          className="text-sm text-[#1B4332] font-medium hover:underline">
                    {cfg.enabled ? "Disable" : "Enable"}
                  </button>
                } />

          <Row label="Endpoint URL"
                value={
                  <CopyBox testId="webhook-endpoint-url"
                            text={cfg.endpoint_url || cfg.endpoint_path} />
                } />

          {cfg.enabled && (
            <>
              <Row label="Secret"
                    value={
                      showSecretOnce
                        ? <CopyBox testId="webhook-secret-once" text={secretOnce} />
                        : <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <KeyRound className="w-3.5 h-3.5" />
                            stored encrypted · use Regenerate to reveal a new one
                          </span>
                    }
                    action={
                      <button onClick={rotate} disabled={busy}
                              data-testid="webhook-regenerate-secret"
                              className="inline-flex items-center gap-1 text-sm text-rose-700 hover:underline">
                        <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                      </button>
                    } />
              <Row label="Last inbound"
                    value={
                      <span className="text-xs text-slate-600">
                        {cfg.last_inbound_at
                          ? `${cfg.last_inbound_at.slice(0, 19).replace("T", " ")}: ${cfg.last_inbound_status}`
                          : "Never"}
                        {cfg.last_inbound_error &&
                          <span className="ml-2 text-rose-700">({cfg.last_inbound_error})</span>}
                      </span>
                    } />

              <SamplePayload exampleBody={exampleBody} cfg={cfg} />
            </>
          )}

          <hr className="border-slate-200" />

          <section className="space-y-3" data-testid="outbound-section">
            <h3 className="font-display text-base font-semibold">Outbound webhooks</h3>
            <p className="text-xs text-slate-500">
              We'll POST a signed JSON body to your URL whenever the selected
              events happen. Retries 5x with backoff. Internal notes are
              never sent.
            </p>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Destination URL
              </span>
              <input
                value={outboundUrl}
                onChange={(e) => setOutboundUrl(e.target.value)}
                data-testid="outbound-url"
                placeholder="https://hook.example.com/booking-events"
                className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
              />
            </label>
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Events to subscribe
              </legend>
              <div className="mt-1 grid sm:grid-cols-2 gap-1.5">
                {ALL_EVENTS.map((ev) => (
                  <label key={ev}
                          className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox"
                            data-testid={`outbound-event-${ev}`}
                            checked={outboundEvents.includes(ev)}
                            onChange={(e) => {
                              setOutboundEvents((cur) =>
                                e.target.checked
                                  ? [...new Set([...cur, ev])]
                                  : cur.filter((x) => x !== ev));
                            }} />
                    <code className="text-xs">{ev}</code>
                  </label>
                ))}
              </div>
            </fieldset>
            <button onClick={saveOutbound} disabled={busy}
                    data-testid="outbound-save"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Save outbound
            </button>

            {log.length > 0 && (
              <details className="text-xs text-slate-600 mt-2" data-testid="outbound-log">
                <summary className="cursor-pointer font-medium">
                  Delivery history ({log.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {log.map((row) => (
                    <li key={row.id}>
                      {row.created_at?.slice(0, 19).replace("T", " ")}: {row.event} →{" "}
                      {row.success ? <span className="text-emerald-700">OK</span>
                                    : <span className="text-rose-700">FAIL {row.error}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        </>
      )}

      <Link to="/docs/WEBHOOKS_INTEGRATION.md"
             data-testid="webhook-setup-doc"
             className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-700">
        Zapier / Make / website-form integration guide <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Row({ label, value, action }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-sm text-slate-700 flex items-center gap-2">
        <span className="text-xs text-slate-500 uppercase tracking-[0.18em] font-semibold w-32">
          {label}
        </span>
        {value}
      </div>
      {action}
    </div>
  );
}

function CopyBox({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs">
      <code data-testid={testId} className="text-slate-700">{text}</code>
      <button onClick={onCopy}
              className="p-1 text-slate-500 hover:text-emerald-700">
        {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
}

function SamplePayload({ exampleBody, cfg }) {
  return (
    <details className="text-xs text-slate-600" data-testid="webhook-sample">
      <summary className="cursor-pointer font-medium">Sample request</summary>
      <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto">
{`POST ${cfg.endpoint_url || cfg.endpoint_path}
Content-Type: application/json
X-ConfirmPh-Signature: sha256=<HMAC_SHA256(secret, "<iso ts>.<raw body>")>
X-ConfirmPh-Timestamp: 2026-09-05T01:00:00+00:00
X-ConfirmPh-Delivery-Id: <your unique id, used for replay dedupe>

${exampleBody}`}
      </pre>
    </details>
  );
}
